// System console: read-only identity federation summary for one organisation, with an emergency
// provider disable (ADR-011). Configuration belongs to the organisation's administrators.
import type { PlatformIdentitySummary } from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, get } from '../api.ts';
import { ErrorState, ErrorSummary, Field, Loading, StatusBadge } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';

const P = '/api/platform/v1';

export function PlatformIdentityPanel({ tenantId }: { tenantId: string }) {
  const { t, dateTime } = useI18n();
  const qc = useQueryClient();
  const key = ['platform', 'tenant', tenantId, 'identity'];
  const q = useQuery({ queryKey: key, queryFn: () => get<PlatformIdentitySummary>(`${P}/tenants/${tenantId}/identity`) });
  const [reason, setReason] = useState('');
  const disable = useMutation({
    mutationFn: (p: { id: string; version: number }) => api('POST', `${P}/tenants/${tenantId}/identity/providers/${p.id}/disable`, { expectedVersion: p.version, reason }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  return (
    <section className="panel" aria-labelledby="pid-title">
      <div className="panel-head">
        <div>
          <h2 id="pid-title">{t('platform.identity.title')}</h2>
          <p>{t('platform.identity.sub')}</p>
        </div>
      </div>
      <div className="pad stack">
        {q.isPending ? <Loading /> : q.isError ? <ErrorState error={q.error} /> : (
          <>
            <ErrorSummary error={disable.error} fieldIds={{}} />
            <h3>{t('platform.identity.providers')}</h3>
            {q.data.providers.length === 0 ? <p>{t('platform.identity.none')}</p> : (
              <ul className="stack">
                {q.data.providers.map((p) => (
                  <li key={p.id} className="row">
                    <span>
                      <strong>{p.displayName}</strong> · {t(p.protocol === 'oidc' ? 'identity.protocol.oidc' : 'identity.protocol.saml')} · <StatusBadge status={p.status} /> · {t('identity.col.linked')}: {p.linkedUsers}
                    </span>
                    {p.status !== 'disabled' ? (
                      <button className="button small danger" type="button" disabled={!reason.trim() || disable.isPending} onClick={() => disable.mutate({ id: p.id, version: p.version })}>
                        {t('platform.identity.disable')}
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {q.data.providers.some((p) => p.status !== 'disabled') ? (
              <Field label={t('common.reason')} hint={t('common.reasonHint')}>
                {(p) => <input {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />}
              </Field>
            ) : null}
            <h3>{t('platform.identity.tokens')}</h3>
            <ul className="stack">
              {q.data.scimTokens.map((tok) => (
                <li key={tok.id}>
                  {tok.label} · <code data-ltr>…{tok.hint}</code> · {tok.lastUsedAt ? dateTime(tok.lastUsedAt) : t('identity.detail.never')} {tok.revokedAt ? <StatusBadge status="revoked" /> : null}
                </li>
              ))}
            </ul>
            <h3>{t('platform.identity.directories')}</h3>
            <ul className="stack">
              {q.data.directories.map((d) => (
                <li key={d.id}>
                  {d.displayName} · <StatusBadge status={d.status} /> {d.lastOutcome ? <StatusBadge status={d.lastOutcome} /> : null}
                </li>
              ))}
            </ul>
            <p>{t('platform.identity.elevated')}: {q.data.elevatedMappingRules}</p>
            <p>{t('platform.identity.provisioned', { scim: q.data.provisionedUsers.scim, directory: q.data.provisionedUsers.directory, jit: q.data.provisionedUsers.ssoJit })}</p>
          </>
        )}
      </div>
    </section>
  );
}
